import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrmTelegramAccountsDto, CrmTelegramAuthStartDto, CrmTelegramAuthStepDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { useState } from 'react';
import { api } from '../../../app/api-client';
import { crmCitiesOptions } from '../queries';

export function CrmTelegramConnectPanel() {
  const queryClient = useQueryClient();
  const { data: cities } = useQuery(crmCitiesOptions());
  const accountsQuery = useQuery({
    queryKey: ['admin', 'crm', 'telegram', 'accounts'],
    queryFn: () =>
      api
        .get('admin/crm/messaging/telegram/accounts')
        .json()
        .then((d) => CrmTelegramAccountsDto.parse(d)),
  });

  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [authSessionId, setAuthSessionId] = useState<string | null>(null);
  const [step, setStep] = useState<'idle' | 'code' | 'password'>('idle');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const start = useMutation({
    mutationFn: (): Promise<CrmTelegramAuthStartDto> =>
      api
        .post('admin/crm/messaging/telegram/auth/start', {
          json: { phone, label, cityIds },
        })
        .json()
        .then((d) => CrmTelegramAuthStartDto.parse(d)),
    onSuccess: (data) => {
      setAuthSessionId(data.authSessionId);
      setStep('code');
    },
  });

  const submitCode = useMutation({
    mutationFn: () =>
      api
        .post('admin/crm/messaging/telegram/auth/code', {
          json: { authSessionId, code },
        })
        .json()
        .then((d) => CrmTelegramAuthStepDto.parse(d)),
    onSuccess: (res) => {
      if (res.step === 'awaiting_password') {
        setStep('password');
        return;
      }
      resetWizard();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'telegram'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'messaging'] });
    },
  });

  const submitPassword = useMutation({
    mutationFn: () =>
      api
        .post('admin/crm/messaging/telegram/auth/password', {
          json: { authSessionId, password },
        })
        .json()
        .then((d) => CrmTelegramAuthStepDto.parse(d)),
    onSuccess: () => {
      resetWizard();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'telegram'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'messaging'] });
    },
  });

  const disconnect = useMutation({
    mutationFn: (accountId: string) =>
      api.post(`admin/crm/messaging/telegram/accounts/${accountId}/disconnect`),
    onSuccess: () => void accountsQuery.refetch(),
  });

  const [reassignUserId, setReassignUserId] = useState<Record<string, string>>({});

  const reassign = useMutation({
    mutationFn: ({ accountId, responsibleUserId }: { accountId: string; responsibleUserId: string }) =>
      api.patch(`admin/crm/messaging/telegram/accounts/${accountId}/responsible`, {
        json: { responsibleUserId },
      }),
    onSuccess: () => void accountsQuery.refetch(),
  });

  function resetWizard() {
    setStep('idle');
    setAuthSessionId(null);
    setCode('');
    setPassword('');
  }

  function toggleCity(id: string) {
    setCityIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }

  return (
    <div className="mx-auto max-w-xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Telegram MTProto</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Личный аккаунт менеджера для двусторонних переписок в CRM. Код и пароль не сохраняются в логах.
        </p>
      </header>

      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="font-medium">Подключённые аккаунты</h2>
        {accountsQuery.data?.items.length ? (
          <ul className="space-y-2">
            {accountsQuery.data.items.map((acc) => (
              <li
                key={acc.id}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{acc.label}</p>
                  <p className="text-muted-foreground">
                    {acc.externalAccountId} · {acc.status}
                  </p>
                  <p className="text-muted-foreground">
                    Ответственный: {acc.responsibleDisplayName ?? 'не назначен'}
                  </p>
                  {acc.status === 'ACTIVE' ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        className="min-w-[12rem] flex-1 rounded-md border px-2 py-1 text-xs"
                        placeholder="UUID менеджера CRM"
                        value={reassignUserId[acc.id] ?? ''}
                        onChange={(e) =>
                          setReassignUserId((prev) => ({ ...prev, [acc.id]: e.target.value.trim() }))
                        }
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={!reassignUserId[acc.id] || reassign.isPending}
                        onClick={() =>
                          reassign.mutate({
                            accountId: acc.id,
                            responsibleUserId: reassignUserId[acc.id] ?? '',
                          })
                        }
                      >
                        Передать
                      </Button>
                    </div>
                  ) : null}
                </div>
                {acc.status === 'ACTIVE' ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disconnect.isPending}
                    onClick={() => disconnect.mutate(acc.id)}
                  >
                    Отключить
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Аккаунтов пока нет.</p>
        )}
      </section>

      {step === 'idle' ? (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="font-medium">Новый аккаунт</h2>
          <label className="block text-sm">
            Телефон
            <input
              className="mt-1 w-full rounded-md border px-3 py-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+995…"
            />
          </label>
          <label className="block text-sm">
            Метка
            <input
              className="mt-1 w-full rounded-md border px-3 py-2"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Менеджер — Тбилиси"
            />
          </label>
          <div>
            <p className="text-sm font-medium">Города</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {cities?.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`rounded-md border px-2 py-1 text-sm ${cityIds.includes(c.id) ? 'bg-primary text-primary-foreground' : ''}`}
                    onClick={() => toggleCity(c.id)}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <Button
            type="button"
            disabled={!phone.trim() || !label.trim() || cityIds.length === 0 || start.isPending}
            onClick={() => start.mutate()}
          >
            {start.isPending ? 'Отправка…' : 'Отправить код в Telegram'}
          </Button>
        </section>
      ) : null}

      {step === 'code' ? (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="font-medium">Код из Telegram</h2>
          <input
            className="w-full rounded-md border px-3 py-2"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={resetWizard}>
              Отмена
            </Button>
            <Button
              type="button"
              disabled={!code.trim() || submitCode.isPending}
              onClick={() => submitCode.mutate()}
            >
              Подтвердить
            </Button>
          </div>
        </section>
      ) : null}

      {step === 'password' ? (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="font-medium">Пароль 2FA Telegram</h2>
          <input
            type="password"
            className="w-full rounded-md border px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <Button
            type="button"
            disabled={!password || submitPassword.isPending}
            onClick={() => submitPassword.mutate()}
          >
            Войти
          </Button>
        </section>
      ) : null}
    </div>
  );
}
